// Payment disclosure text for the tabs widget — the compliant wording every displayed
// payment carries (Reg Z / Reg M triggered terms). Shared by two bundles so the wording
// can't drift between them:
//   - pmdrs.js: TabsWidget imports it.
//   - pmdra-ddc.js: the Dealer.com VDP renders a lightweight copy of the tabs widget
//     before any interaction (Dealer.com's <10 KB budget). That file isn't bundled, so
//     gulp's copy-external-script inlines this file into it (dropping the `export`s).
// Keep it dependency-free: no imports, plain functions only.


//same output as FormatHelper.currencyFormat
export function currency(number, decimals) {
    decimals = decimals == null ? 2 : decimals;
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(number);
}

//"2026-09-30..." -> "9/30/26"; anything else is shown as it came
export function formatOfferEndDate(expirationDate) {
    if (!expirationDate) return "";
    let match = String(expirationDate).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return String(expirationDate);
    //LenderDesk sends 0001-01-01 when a quote has no expiration
    if (Number(match[1]) < 2000) return "";
    return `${Number(match[2])}/${Number(match[3])}/${match[1].slice(2)}`;
}

export function formatMiles(miles) {
    return Number(miles).toLocaleString("en-US");
}

//pre-tax total due at signing, same value the deal page shows with taxes off
export function leaseDueAtSigning(paymentObject) {
    return paymentObject.dueAtSignBase ?? paymentObject.dueAtSign ?? 0;
}

export function stockSentence(stockNumber) {
    return stockNumber ? `Stock ${stockNumber}.` : "";
}

//LenderDesk/PmRates credit tiers (LenderDeskDriver.getCreditRanges), same as pm-drs-api's DisclaimerHelper
const CREDIT_TIERS = [[770, 850], [720, 769], [680, 719], [650, 679]];

//payments are quoted at the top of a tier (850); the tier is named by its lowest score: "770+"
export function creditTierFloor(creditRating) {
    let score = Number(creditRating);
    let tier = CREDIT_TIERS.find(([min, max]) => min <= score && score <= max);
    return tier ? tier[0] : score;
}

export function creditSentence(paymentObject, qualifiers) {
    let creditRating = paymentObject.creditRating;
    return creditRating
        ? `On approved credit, ${creditTierFloor(creditRating)}+ tier; not all ${qualifiers} qualify.`
        : `On approved credit; not all ${qualifiers} qualify.`;
}

export function offerEndsSentence(paymentObject) {
    let offerEnds = formatOfferEndDate(paymentObject.expirationDate);
    return offerEnds ? `Offer ends ${offerEnds}.` : "";
}

//Vehicle Code §11713.1(c)(2) charges, spelled out (no abbreviations). includedDocFee > 0 when
//the advertised figure already includes the dealer's doc fee, which then has to be named.
export function feesSentence(includedDocFee) {
    if (includedDocFee > 0) {
        let amount = currency(includedDocFee, Number.isInteger(Number(includedDocFee)) ? 0 : 2);
        return `Includes the ${amount} dealer document processing charge. Plus government fees and taxes, any finance charges, any electronic filing charge, and any emission testing charge.`;
    }
    return "Plus government fees and taxes, any finance charges, any dealer document processing charge, any electronic filing charge, and any emission testing charge.";
}

//lampedodge bakes the doc fee into the financed price; everyone else's financed price excludes it
export function financedIncludedDocFee(paymentObject, accountId) {
    if (accountId !== 'lampedodge') return 0;
    return paymentObject.docFeeDisplay ?? paymentObject.docFee ?? paymentObject.docFeeForTotal ?? 0;
}

// ctx: { stockNumber, accountId }
export function financeDisclaimerText(paymentObject, ctx, monthlyPayment, apr, down) {
    let parts = [stockSentence(ctx.stockNumber)];
    let totalPrice = currency(paymentObject.purchasePrice, 0);
    parts.push(`Estimated payment ${monthlyPayment}/mo for ${paymentObject.term} months at ${apr}% Annual Percentage Rate, based on Total Price ${totalPrice} less ${down} down.`);
    parts.push(feesSentence(financedIncludedDocFee(paymentObject, ctx.accountId)));
    parts.push(creditSentence(paymentObject, "buyers"));
    parts.push(offerEndsSentence(paymentObject));
    return parts.filter(Boolean).join(" ");
}

//Easy Ride / Premier / Triple Financing: same as finance, plus the final (balloon) payment
export function balloonDisclaimerText(paymentObject, ctx, monthlyPayment, balloonPayment, apr, down) {
    let parts = [stockSentence(ctx.stockNumber)];
    let totalPrice = currency(paymentObject.purchasePrice, 0);
    parts.push(`Estimated payment ${monthlyPayment}/mo for ${paymentObject.term} months at ${apr}% Annual Percentage Rate, plus one final payment of ${balloonPayment} at the end of the term, based on Total Price ${totalPrice} less ${down} down.`);
    parts.push(feesSentence(financedIncludedDocFee(paymentObject, ctx.accountId)));
    parts.push(creditSentence(paymentObject, "buyers"));
    parts.push(offerEndsSentence(paymentObject));
    return parts.filter(Boolean).join(" ");
}

export function leaseDisclaimerText(paymentObject, ctx, monthlyPayment, dueAtSigning) {
    let term = paymentObject.term;
    let parts = [stockSentence(ctx.stockNumber)];

    let terms = `${term}-month closed-end lease, ${term} payments of ${monthlyPayment}, ${dueAtSigning} due at signing`;
    let securityDeposit = paymentObject.securityDeposit;
    if (securityDeposit === 0) terms += ", no security deposit required";
    else if (securityDeposit > 0) terms += `, ${currency(securityDeposit)} security deposit`;
    parts.push(terms + ".");

    //only quote a per-mile rate the API actually gave us
    let miles = paymentObject.mileageLimit > 0 ? `${formatMiles(paymentObject.mileageLimit)} miles/yr` : "";
    if (miles && paymentObject.perMileCharge > 0) {
        parts.push(`${miles}, ${currency(paymentObject.perMileCharge)} per additional mile; lessee responsible for excess wear and use.`);
    } else if (miles) {
        parts.push(`${miles}; lessee responsible for excess mileage, wear and use.`);
    } else {
        parts.push("Lessee responsible for excess mileage, wear and use.");
    }

    parts.push("Monthly payment excludes sales tax; title, license and registration are due at signing.");
    parts.push(`Based on Total Price ${currency(paymentObject.purchasePrice, 0)}.`);
    parts.push(feesSentence(0));
    parts.push(creditSentence(paymentObject, "lessees"));
    parts.push(offerEndsSentence(paymentObject));
    return parts.filter(Boolean).join(" ");
}

export function cashDisclaimerText(paymentObject, ctx) {
    let parts = [stockSentence(ctx.stockNumber)];
    //same figure the panel headline shows, which already folds in the doc fee
    let totalPrice = paymentObject.finalPriceDisplay ?? paymentObject.payment;
    parts.push(`Total Price ${currency(totalPrice, 0)}.`);
    parts.push("See pricing detail on this page for MSRP, discounts and available offers.");

    let includedDocFee = paymentObject.finalPriceDisplay != null
        ? paymentObject.finalPriceDisplay - (paymentObject.payment ?? paymentObject.finalPriceDisplay)
        : 0;
    parts.push(feesSentence(includedDocFee));

    parts.push(offerEndsSentence(paymentObject));
    return parts.filter(Boolean).join(" ");
}

// Headline figure, subtitle and disclaimer for one panel — what TabsWidget.
// displayPaymentDisclosureTexts renders. headline is null for cash (its figure is the price).
export function panelTexts(paymentObject, type, ctx) {
    let apr = parseFloat(paymentObject.moneyFactor).toFixed(2);
    if (type == "balloon") {
        let monthlyPayment = currency(paymentObject.monthlyPayment);
        let balloonPayment = currency(paymentObject.balloonPayment);
        let down = currency(Number(paymentObject.downPayment) || 0, 0);
        return {
            headline: monthlyPayment,
            subtitle: `${paymentObject.term} months · ${apr}% Annual Percentage Rate · ${down} down · ${balloonPayment} final payment`,
            disclaimer: balloonDisclaimerText(paymentObject, ctx, monthlyPayment, balloonPayment, apr, down),
        };
    }
    if (type == "finance") {
        let monthlyPayment = currency(paymentObject.monthlyPayment);
        let down = currency(Number(paymentObject.downPayment) || 0, 0);
        return {
            headline: monthlyPayment,
            subtitle: `${paymentObject.term} months · ${apr}% Annual Percentage Rate · ${down} down`,
            disclaimer: financeDisclaimerText(paymentObject, ctx, monthlyPayment, apr, down),
        };
    }
    if (type == "lease") {
        let monthlyPayment = currency(paymentObject.monthlyPayment);
        let dueAtSigning = currency(leaseDueAtSigning(paymentObject));
        let subtitle = [`${paymentObject.term} months`, `${dueAtSigning} due at signing`];
        if (paymentObject.mileageLimit > 0) subtitle.push(`${formatMiles(paymentObject.mileageLimit)} mi/yr`);
        return {
            headline: monthlyPayment,
            subtitle: subtitle.join(" · "),
            disclaimer: leaseDisclaimerText(paymentObject, ctx, monthlyPayment, dueAtSigning),
        };
    }
    if (type == "cash") {
        return { headline: null, subtitle: null, disclaimer: cashDisclaimerText(paymentObject, ctx) };
    }
    return null;
}

// LenderDesk API payment -> the fields above read (same mapping as
// LenderDeskDriver.formatApiPayment, which uses this for them).
export function lenderDeskRate(apiPayment, dealType) {
    return dealType === 'lease'
        ? Number(apiPayment.interestRate).toFixed(2)          // lease: interestRate is already a percentage (e.g. 2.4 = 2.4%)
        : (apiPayment.originalRate * 100).toFixed(2);         // finance: originalRate is a decimal fraction (e.g. 0.059 = 5.9%)
}

export function lenderDeskDisplayPayment(apiPayment, dealType) {
    return {
        dealType: dealType,
        moneyFactor: lenderDeskRate(apiPayment, dealType),
        monthlyPayment: apiPayment.payment,
        payment: apiPayment.payment,
        downPayment: apiPayment.downPayment,
        term: apiPayment.term,
        expirationDate: apiPayment.expirationDate,
        creditRating: apiPayment.beacon,
        purchasePrice: apiPayment.purchasePrice,
        docFeeForTotal: apiPayment.docFeeForTotal,
        docFeeDisplay: apiPayment.docFeeDisplay,
        finalPriceDisplay: apiPayment.finalPriceDisplay,
        dueAtSignBase: apiPayment.dueAtSignBase,
        docFee: apiPayment.docFee || 0,
        dueAtSign: apiPayment.dueAtSign || 0,
        mileageLimit: apiPayment.mileageLimit ?? null,
        perMileCharge: apiPayment.perMileCharge ?? null,
        securityDeposit: apiPayment.securityDeposit ?? null,
    };
}
